"use client";

import type { RefObject } from "react";
import {
  CHART_AMBER,
  CHART_BLUE,
  CHART_GREEN,
  CHART_INK,
  CHART_RED,
  Chart,
} from "@/components/tools/stats-clinical/chart";
import type { SurvivalCurve } from "@/lib/tool-kit/stats-clinical/kaplan-meier";
import { niceTicks } from "@/lib/tool-kit/stats-clinical/ticks";

/*
 * The Kaplan–Meier plot: one step curve per group with optional shaded confidence bands
 * and tick marks at censored times. Colours are hex attributes so the exported SVG and PNG
 * match the page; each curve also has its own dash pattern so groups stay distinct in
 * greyscale.
 */

export const KM_COLOURS = [
  CHART_BLUE,
  CHART_RED,
  CHART_GREEN,
  CHART_AMBER,
  "#7c3aed",
  "#0e7490",
];
export const KM_DASHES = [
  undefined,
  "7 4",
  "2 3",
  "9 3 2 3",
  "4 2",
  "1 3",
] as const;

function stepPath(
  curve: SurvivalCurve,
  x: (v: number) => number,
  y: (v: number) => number
): string {
  let level = 1;
  const parts = [`M${x(0).toFixed(2)} ${y(1).toFixed(2)}`];
  for (const row of curve.rows) {
    if (row.events > 0) {
      parts.push(`L${x(row.time).toFixed(2)} ${y(level).toFixed(2)}`);
      level = row.survival;
      parts.push(`L${x(row.time).toFixed(2)} ${y(level).toFixed(2)}`);
    }
  }
  parts.push(`L${x(curve.maxTime).toFixed(2)} ${y(level).toFixed(2)}`);
  return parts.join(" ");
}

/** A shaded band between the lower and upper interval curves, as a closed step polygon. */
function bandPath(
  curve: SurvivalCurve,
  x: (v: number) => number,
  y: (v: number) => number
): string {
  const events = curve.rows.filter((row) => row.events > 0);
  const lowerSteps: [number, number][] = [[0, 1]];
  const upperSteps: [number, number][] = [[0, 1]];
  let lower = 1;
  let upper = 1;
  for (const row of events) {
    lowerSteps.push([row.time, lower]);
    upperSteps.push([row.time, upper]);
    lower = Number.isFinite(row.lower) ? row.lower : 0;
    upper = Number.isFinite(row.upper) ? row.upper : upper;
    lowerSteps.push([row.time, lower]);
    upperSteps.push([row.time, upper]);
  }
  lowerSteps.push([curve.maxTime, lower]);
  upperSteps.push([curve.maxTime, upper]);
  const along = (points: [number, number][]) =>
    points.map(([t, v]) => `${x(t).toFixed(2)} ${y(v).toFixed(2)}`);
  return `M${[...along(upperSteps), ...along(lowerSteps.reverse())].join(" L")} Z`;
}

export function SurvivalPlot({
  curves,
  names,
  showBands,
  showCensored,
  timeUnit,
  timeLabel,
  survivalLabel,
  label,
  formatNumber,
  svgRef,
  idPrefix = "km",
}: {
  curves: readonly SurvivalCurve[];
  names: readonly string[];
  showBands: boolean;
  showCensored: boolean;
  timeUnit: string;
  timeLabel: string;
  survivalLabel: string;
  label: string;
  formatNumber: (value: number) => string;
  svgRef: RefObject<SVGSVGElement | null>;
  idPrefix?: string;
}) {
  const maxTime = Math.max(...curves.map((c) => c.maxTime), 1);
  const ticks = niceTicks(0, maxTime);
  return (
    <Chart
      formatX={formatNumber}
      formatY={(v) => formatNumber(Math.round(v * 100) / 100)}
      height={440}
      idPrefix={idPrefix}
      label={label}
      svgRef={svgRef}
      xDomain={[0, ticks.max]}
      xLabel={timeUnit ? `${timeLabel} (${timeUnit})` : timeLabel}
      yDomain={[0, 1]}
      yLabel={survivalLabel}
    >
      {(area) => (
        <>
          {showBands
            ? curves.map((curve, i) => (
                <path
                  d={bandPath(curve, area.x, area.y)}
                  fill={KM_COLOURS[i % KM_COLOURS.length]}
                  fillOpacity={0.13}
                  // biome-ignore lint/suspicious/noArrayIndexKey: curves have no ids
                  key={`band${i}`}
                />
              ))
            : null}
          {curves.map((curve, i) => (
            <path
              d={stepPath(curve, area.x, area.y)}
              fill="none"
              // biome-ignore lint/suspicious/noArrayIndexKey: curves have no ids
              key={`line${i}`}
              stroke={KM_COLOURS[i % KM_COLOURS.length]}
              strokeDasharray={KM_DASHES[i % KM_DASHES.length]}
              strokeLinejoin="round"
              strokeWidth={2.2}
            />
          ))}
          {showCensored
            ? curves.map((curve, i) =>
                curve.rows
                  .filter((row) => row.censored > 0)
                  .map((row) => (
                    <path
                      d={`M${area.x(row.time)} ${area.y(row.survival) - 5}V${area.y(row.survival) + 5}`}
                      key={`c${i}-${row.time}`}
                      stroke={KM_COLOURS[i % KM_COLOURS.length]}
                      strokeWidth={1.6}
                    />
                  ))
              )
            : null}
          {curves.length > 1
            ? names.map((name, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: legend rows are positional
                <g key={`legend${i}`}>
                  <line
                    stroke={KM_COLOURS[i % KM_COLOURS.length]}
                    strokeDasharray={KM_DASHES[i % KM_DASHES.length]}
                    strokeWidth={2.2}
                    x1={area.right - 170}
                    x2={area.right - 142}
                    y1={area.top + 18 + i * 18}
                    y2={area.top + 18 + i * 18}
                  />
                  <text
                    fill={CHART_INK}
                    fontFamily="system-ui, sans-serif"
                    fontSize={12}
                    x={area.right - 134}
                    y={area.top + 22 + i * 18}
                  >
                    {name}
                  </text>
                </g>
              ))
            : null}
        </>
      )}
    </Chart>
  );
}
